import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteAllIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import MainLayout from '../layouts/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { useOperatorMessages } from '../contexts/OperatorMessageContext';
import { isSuperAdminUser } from '../utils/adminAccess';
import {
  OPERATOR_MESSAGE_CATEGORIES,
  OperatorMessage,
  OperatorMessageCategory,
  OperatorMessageDetail,
  operatorMessageService,
} from '../services/operatorMessageService';
import { formatDateTimeKst } from '../utils/dateUtils';
import OperatorMessageComposeTime from '../components/monitoring/OperatorMessageComposeTime';

function isMainAdmin(user?: { is_main_admin?: boolean; is_superuser?: boolean } | null) {
  return Boolean(user?.is_main_admin || user?.is_superuser);
}

const emptyForm = {
  category: 'notice' as OperatorMessageCategory,
  title: '',
  body: '',
};

function displayName(u: {
  full_name?: string | null;
  username?: string | null;
  user_id?: number;
}) {
  return u.full_name || u.username || (u.user_id != null ? `#${u.user_id}` : '-');
}

export type OperatorMessagesPanelProps = {
  /** 중앙제어 등 상위 레이아웃에 끼울 때 true (사이드메뉴 없음) */
  embedded?: boolean;
};

export const OperatorMessagesPanel: React.FC<OperatorMessagesPanelProps> = ({
  embedded = false,
}) => {
  const { authState } = useAuth();
  const user = authState.user;
  const mainAdmin = isMainAdmin(user);
  const { enqueueSnackbar } = useSnackbar();
  const { refreshUnread } = useOperatorMessages();
  const [items, setItems] = useState<OperatorMessage[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OperatorMessage | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<OperatorMessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await operatorMessageService.list(categoryFilter || undefined);
      setItems(res.items || []);
      setSelected(new Set());
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '목록을 불러오기 실패', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const canEditRow = useCallback(
    (row: OperatorMessage) => {
      if (mainAdmin) return true;
      return row.author_id != null && user?.id != null && Number(row.author_id) === Number(user.id);
    },
    [mainAdmin, user?.id]
  );

  const allIds = useMemo(() => items.map((r) => r.id), [items]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const toggleOne = (id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row: OperatorMessage, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditing(row);
    setForm({
      category: (row.category as OperatorMessageCategory) || 'notice',
      title: row.title,
      body: row.body,
    });
    setDialogOpen(true);
  };

  const openDetail = async (row: OperatorMessage) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      if (!row.is_read) {
        try {
          await operatorMessageService.markRead(row.id);
          refreshUnread?.();
        } catch {
          /* ignore mark-read failure for detail view */
        }
      }
      const d = await operatorMessageService.getDetail(row.id);
      setDetail(d);
      setItems((prev) =>
        prev.map((it) => (it.id === row.id ? { ...it, is_read: true } : it))
      );
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '상세를 불러오기 실패', {
        variant: 'error',
      });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      enqueueSnackbar('제목과 내용을 입력', { variant: 'warning' });
      return;
    }
    try {
      if (editing) {
        await operatorMessageService.update(editing.id, form);
        enqueueSnackbar('수정됨', { variant: 'success' });
      } else {
        await operatorMessageService.create(form);
        enqueueSnackbar('발송됨', { variant: 'success' });
      }
      setDialogOpen(false);
      load();
      refreshUnread?.();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '저장 실패', { variant: 'error' });
    }
  };

  const removeOne = async (row: OperatorMessage, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!mainAdmin) return;
    if (!window.confirm(`삭제 확인\n${row.title}`)) return;
    try {
      await operatorMessageService.remove(row.id);
      enqueueSnackbar('삭제 완료', { variant: 'success' });
      load();
      refreshUnread?.();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || '삭제 실패', { variant: 'error' });
    }
  };

  const removeSelected = async () => {
    if (!mainAdmin || selected.size === 0) return;
    if (!window.confirm(`선택한 ${selected.size}건 삭제 확인`)) return;
    try {
      const n = await operatorMessageService.removeBulk(Array.from(selected));
      enqueueSnackbar(`${n}건 삭제 완료`, { variant: 'success' });
      load();
      refreshUnread?.();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '선택 삭제 실패', { variant: 'error' });
    }
  };

  const removeAll = async () => {
    if (!mainAdmin) return;
    if (!window.confirm('모든 관제 메시지 삭제 확인')) return;
    try {
      const n = await operatorMessageService.removeAll();
      enqueueSnackbar(`${n}건 삭제 완료`, { variant: 'success' });
      load();
      refreshUnread?.();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '전체 삭제 실패', { variant: 'error' });
    }
  };

  const filterChips = useMemo(
    () => [{ value: '', label: '전체' }, ...OPERATOR_MESSAGE_CATEGORIES],
    []
  );

  const content = (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">관제 메시지</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Tooltip title="새로고침">
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            작성
          </Button>
          {mainAdmin && (
            <>
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                disabled={selected.size === 0}
                onClick={removeSelected}
              >
                선택삭제{selected.size > 0 ? ` (${selected.size})` : ''}
              </Button>
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteAllIcon />}
                onClick={removeAll}
              >
                전체삭제
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {filterChips.map((c) => (
          <Chip
            key={c.value || 'all'}
            label={c.label}
            color={categoryFilter === c.value ? 'primary' : 'default'}
            onClick={() => setCategoryFilter(c.value)}
            variant={categoryFilter === c.value ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              {mainAdmin && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': '전체 선택' }}
                  />
                </TableCell>
              )}
              <TableCell>카테고리</TableCell>
              <TableCell>제목</TableCell>
              <TableCell>작성자</TableCell>
              <TableCell>작성시각</TableCell>
              <TableCell>읽음</TableCell>
              <TableCell align="right">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={mainAdmin ? 7 : 6}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    {loading ? '불러오는 중…' : '메시지 없음'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {items.map((row) => (
              <TableRow
                key={row.id}
                hover
                selected={selected.has(row.id)}
                onClick={() => openDetail(row)}
                sx={{ cursor: 'pointer' }}
              >
                {mainAdmin && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Chip size="small" label={row.category_label || row.category} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {row.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {row.body}
                  </Typography>
                </TableCell>
                <TableCell>{row.author_full_name || row.author_username || '-'}</TableCell>
                <TableCell>{formatDateTimeKst(row.created_at)}</TableCell>
                <TableCell>{row.is_read ? '읽음' : '미읽음'}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  {canEditRow(row) && (
                    <IconButton size="small" onClick={(e) => openEdit(row, e)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {mainAdmin && (
                    <IconButton size="small" color="error" onClick={(e) => removeOne(row, e)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? '메시지 수정' : '메시지 작성'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <OperatorMessageComposeTime createdAt={editing?.created_at} />
            <FormControl fullWidth>
              <InputLabel id="om-cat">카테고리</InputLabel>
              <Select
                labelId="om-cat"
                label="카테고리"
                value={form.category}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category: e.target.value as OperatorMessageCategory,
                  }))
                }
              >
                {OPERATOR_MESSAGE_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="제목"
              fullWidth
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              label="내용"
              fullWidth
              multiline
              minRows={5}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={save}>
            {editing ? '저장' : '발송'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {detail?.title || (detailLoading ? '불러오는 중…' : '메시지 상세')}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && !detail ? (
            <Typography color="text.secondary">불러오는 중…</Typography>
          ) : detail ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={detail.category_label || detail.category} />
                <Typography variant="body2" color="text.secondary">
                  {detail.author_full_name || detail.author_username || '-'}
                  {' · '}
                  {formatDateTimeKst(detail.created_at)}
                </Typography>
              </Stack>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{detail.body}</Typography>
              <Divider />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle2" gutterBottom>
                    읽은 사람 ({detail.readers?.length || 0})
                  </Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {(detail.readers || []).length === 0 && (
                        <ListItem>
                          <ListItemText secondary="없음" />
                        </ListItem>
                      )}
                      {(detail.readers || []).map((u) => (
                        <ListItem key={`r-${u.user_id}`} divider>
                          <ListItemText
                            primary={displayName(u)}
                            secondary={u.read_at ? formatDateTimeKst(u.read_at) : undefined}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle2" gutterBottom>
                    안 읽은 사람 ({detail.unread_users?.length || 0})
                  </Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {(detail.unread_users || []).length === 0 && (
                        <ListItem>
                          <ListItemText secondary="없음" />
                        </ListItem>
                      )}
                      {(detail.unread_users || []).map((u) => (
                        <ListItem key={`u-${u.user_id}`} divider>
                          <ListItemText primary={displayName(u)} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          {detail && canEditRow(detail) && (
            <Button
              onClick={() => {
                setDetailOpen(false);
                openEdit(detail);
              }}
            >
              수정
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)} variant="contained">
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) return content;
  return <MainLayout>{content}</MainLayout>;
};

const OperatorMessagesPage: React.FC = () => {
  const { authState } = useAuth();
  if (!authState.loading && isSuperAdminUser(authState.user)) {
    return <Navigate to="/central-control?tab=messages" replace />;
  }
  return <OperatorMessagesPanel />;
};

export default OperatorMessagesPage;
